import {Component, computed, input, model} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ItemDto} from '../../../models/item-dto';
import {UnitConverter} from '../../../enums/unit';

/**
 * Amount field for a composition component ("Bestandteil") — the *Einheit* toggle, the unit prefix, the
 * amount input and (optionally) a price input, rendered as one bordered control.
 *
 * Extracted from the two places that carried an identical, hand-rolled copy of this markup: the items
 * slide-over (`items/items-index`) and the interchangeable-group widget. Both used to position the unit
 * prefix as an **absolute overlay** and push its measured width back into the input's `padding-left` from
 * inside the template (`[style.padding-left.px]="amount.getBoundingClientRect().width + 5"`). That read
 * ran on every change-detection cycle (a synchronous layout read — layout thrashing, and a textbook
 * `ExpressionChangedAfterItHasBeenCheckedError` source) and, worse, always reported the *previous* cycle's
 * prefix: right after the *Einheit* toggle the padding was still sized for the short bare-unit prefix
 * (`ml`) while the wide one (`330 ml x`) was already painted, so the typed value rendered underneath it
 * and collided with the price field (Bug #230).
 *
 * Here the prefix is a plain flex item next to the input, so the browser does the measuring. Nothing is
 * measured in JS, nothing is pinned to a magic pixel width, and the row reflows instead of overlapping.
 */
@Component({
  selector: 'app-unit-amount-field',
  templateUrl: './unit-amount-field.component.html',
  styleUrl: './unit-amount-field.component.scss',
  imports: [FormsModule]
})
export class UnitAmountFieldComponent {
  /** The item the amount refers to — supplies the unit and (for unit mode) its volume per unit. */
  readonly item = input<ItemDto | undefined>(undefined);

  /** Render the price input beside the amount (the interchangeable-group case). */
  readonly showPrice = input(false);

  /** Disable the whole control (items renders the row before an item has been picked). */
  readonly disabled = input(false);

  /** Draw the warning ring — the items page marks a category-filtered component this way. */
  readonly highlight = input(false);

  /** Row index, used only to keep the `name` attributes unique within a repeated list. */
  readonly index = input(0);

  /** Amount typed by the user. */
  readonly value = model<number | undefined>(undefined);

  /** Price override; only bound when {@link showPrice} is set. */
  readonly price = model<number | undefined>(undefined);

  /** `true` ⇒ the amount counts *units* (`330 ml x`), `false` ⇒ it is a bare quantity in the item's unit. */
  readonly unit = model(false);

  /**
   * Text shown in front of the amount. Bare unit label by default; in unit mode the item's volume per unit
   * followed by that label and an `x`. Deliberately EMPTY when unit mode is on but the item has no
   * source detail — the original markup rendered nothing in that case and callers rely on it.
   */
  protected readonly prefix = computed<string>(() => {
    const item = this.item();
    if (!item) return '';

    const unitLabel = UnitConverter.GetString(item.unit);
    if (!this.unit()) return unitLabel;

    const volumePerUnit = item.latestSourceDetail?.volumePerUnit;
    // Joined with U+00A0 so "330 ml x" never breaks, exactly as the old &nbsp;-joined markup did.
    return volumePerUnit ? [volumePerUnit, unitLabel, 'x'].join('\u00A0') : '';
  });

  /** Flips unit mode. A no-op while disabled so the button matches the input's state. */
  protected toggleUnit(): void {
    if (this.disabled()) return;
    this.unit.update(value => !value);
  }
}
